import { useState } from 'react';
import type { StatCardModel } from '@/types/stats';
import type { DomainRow } from '@/data/domains';
import { PageHeader } from '@/components/composites/PageHeader';
import { StatCardGrid } from '@/components/composites/StatCardGrid';
import { FilterBar } from '@/components/composites/FilterBar';
import { DataTable } from '@/components/composites/DataTable';
import { Tabs } from '@/components/composites/Tabs';
import { Select } from '@/components/primitives/Select';
import { useDomains } from '@/hooks/useDomains';
import styles from './DomainAnalyticsPage.module.css';

type DomainMode = 'root' | 'subdomain';

const MODE_OPTIONS = [
  { value: 'root' as const, label: 'Root Domain' },
  { value: 'subdomain' as const, label: 'Subdomain' },
];

const LIMIT_OPTIONS = [
  { value: '50', label: 'Top 50' },
  { value: '100', label: 'Top 100' },
  { value: 'all', label: 'All' },
];

export function DomainAnalyticsPage() {
  const [mode, setMode] = useState<DomainMode>('root');
  const [limit, setLimit] = useState('50');
  const limitArg: number | 'all' = limit === 'all' ? 'all' : Number(limit);
  const { data: domains } = useDomains({
    limit: limitArg,
    groupBySubdomain: mode === 'subdomain',
  });

  const totalHours = domains.reduce((s, d) => s + d.totalHours, 0);
  const totalBillableHours = domains.reduce((s, d) => s + d.billableHours, 0);
  const totalNonBillableHours = domains.reduce((s, d) => s + d.nonBillableHours, 0);
  const totalEvents = domains.reduce((s, d) => s + d.events, 0);
  // Domain-row granularity doesn't carry individual employee IDs, so the
  // best distinct-users proxy available here is the max `employees` across
  // visible rows — which is the upper bound on the firm-wide unique set.
  // A precise firm-wide distinct count would require a separate endpoint
  // and is not justified by this page's mandate.
  const uniqueEmployees = domains.reduce((m, d) => Math.max(m, d.employees), 0);

  const cards: readonly [StatCardModel, StatCardModel, StatCardModel, StatCardModel] = [
    { label: 'Unique Domains', value: domains.length },
    { label: 'Total Hours', value: `${totalHours.toFixed(1)}h` },
    { label: 'Team Members', value: uniqueEmployees },
    { label: 'Total Billable Time', value: `${totalBillableHours.toFixed(1)}h` },
  ];

  return (
    <>
      <PageHeader title="Domain Analytics" />

      <FilterBar
        tabs={
          <Tabs<DomainMode>
            options={MODE_OPTIONS}
            value={mode}
            onChange={setMode}
            variant="segmented"
            ariaLabel="Domain grouping mode"
          />
        }
        extraFilters={
          <Select value={limit} options={LIMIT_OPTIONS} onChange={setLimit} size="sm" />
        }
      />

      <StatCardGrid cards={cards} />

      {/* Task 162 D6 — Rule 13: sortable headers intentionally omitted.
          Row count is bounded by the `LIMIT_OPTIONS` ceiling and the
          backend already returns rows in a meaningful default order
          (top-N by total events). Revisit if user feedback shows a
          desire to re-rank by other columns. */}
      <DataTable<DomainRow>
        caption="Domain analytics"
        columns={[
          {
            key: 'domain',
            header: 'Domain',
            render: (d) => <span className={styles.domainCell}>{d.domain}</span>,
          },
          {
            key: 'app',
            header: 'Application',
            render: (d) => (
              <>
                <span>{d.appName}</span>
                {d.otherAppCount > 0 ? (
                  <span className={styles.appMore}> +{d.otherAppCount} more</span>
                ) : null}
              </>
            ),
          },
          {
            key: 'employees',
            header: 'Distinct Users',
            align: 'right',
            render: (d) => <span className={styles.numCell}>{d.employees}</span>,
          },
          {
            key: 'events',
            header: 'Total Events',
            align: 'right',
            render: (d) => <span className={styles.numCell}>{d.events.toLocaleString()}</span>,
          },
          {
            key: 'avgPerUser',
            header: 'Avg Time/User',
            align: 'right',
            render: (d) => <span className={styles.numCell}>{d.avgHoursPerUser.toFixed(1)}h</span>,
          },
          {
            key: 'billable',
            header: 'Billable Time',
            align: 'right',
            render: (d) => <span className={styles.numCell}>{d.billableHours.toFixed(1)}h</span>,
          },
          {
            key: 'nonBillable',
            header: 'Non-Billable Time',
            align: 'right',
            render: (d) => <span className={styles.numCell}>{d.nonBillableHours.toFixed(1)}h</span>,
          },
          {
            key: 'total',
            header: 'Total Time',
            align: 'right',
            render: (d) => <span className={styles.numCell}>{d.totalHours.toFixed(1)}h</span>,
          },
        ]}
        rows={[...domains]}
        getRowKey={(d) => d.domain}
        footer={
          <tr>
            <td>Total ({domains.length} domains)</td>
            <td>—</td>
            <td className={styles.numCell}>{uniqueEmployees}</td>
            <td className={styles.numCell}>{totalEvents.toLocaleString()}</td>
            <td>—</td>
            <td className={styles.numCell}>{totalBillableHours.toFixed(1)}h</td>
            <td className={styles.numCell}>{totalNonBillableHours.toFixed(1)}h</td>
            <td className={styles.numCell}>{totalHours.toFixed(1)}h</td>
          </tr>
        }
      />
    </>
  );
}
